import React, { useEffect, useState } from "react";
import { api, fmtDate, fmtDateRelative } from "@/lib/api";
import {
  Activity, Plus, RefreshCw, ArrowRightLeft, CheckCircle, Send, DollarSign,
  Users, Briefcase, Server, Wrench, FileText, Trash2,
} from "lucide-react";

const ICONS = {
  pipeline: ArrowRightLeft,
  client: Users,
  project: Briefcase,
  hosting: Server,
  maintenance: Wrench,
  invoice: FileText,
  revenue: DollarSign,
  expense: DollarSign,
  domain: Server,
};

const ACTION_TONE = {
  created: { ring: "ring-emerald-400/30", dot: "bg-emerald-400", Icon: Plus },
  updated: { ring: "ring-sky-400/30", dot: "bg-sky-400", Icon: RefreshCw },
  moved: { ring: "ring-violet-400/30", dot: "bg-violet-400", Icon: ArrowRightLeft },
  paid: { ring: "ring-emerald-400/30", dot: "bg-emerald-400", Icon: CheckCircle },
  sent: { ring: "ring-sky-400/30", dot: "bg-sky-400", Icon: Send },
  deleted: { ring: "ring-rose-400/30", dot: "bg-rose-400", Icon: Trash2 },
};

export default function ActivityFeed({ limit = 12, compact = false }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get("/activity", { params: { limit } });
      setItems(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [limit]); // eslint-disable-line

  return (
    <div data-testid="activity-feed">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <Activity className="w-4 h-4 text-[#007AFF]" />
          <div className="font-display text-xl">Activity</div>
        </div>
        <button onClick={load} className="text-[10px] uppercase tracking-[0.2em] text-white/40 hover:text-white inline-flex items-center gap-1.5" data-testid="activity-refresh">
          <RefreshCw className="w-3 h-3" /> Refresh
        </button>
      </div>

      <div className="space-y-3">
        {loading && <div className="text-xs text-white/30">Loading…</div>}
        {!loading && items.length === 0 && (
          <div className="text-xs text-white/35 text-center py-8 border border-dashed border-white/[0.06] rounded-2xl">
            No activity yet — start by creating a lead or syncing Stripe.
          </div>
        )}
        {items.map((it) => {
          const EntityIcon = ICONS[it.entity_type] || Activity;
          const tone = ACTION_TONE[it.action] || { ring: "ring-white/20", dot: "bg-white/50", Icon: Activity };
          const ActIcon = tone.Icon;
          return (
            <div key={it.id} className="flex items-start gap-3 px-3 py-2.5 rounded-2xl hover:bg-white/[0.03] transition" data-testid={`activity-${it.id}`}>
              <div className={`relative w-9 h-9 rounded-xl bg-white/[0.04] border border-white/[0.06] flex items-center justify-center ring-1 ${tone.ring}`}>
                <EntityIcon className="w-3.5 h-3.5 text-white/70" />
                <span className={`absolute -bottom-1 -right-1 w-4 h-4 rounded-full ${tone.dot} flex items-center justify-center`}>
                  <ActIcon className="w-2 h-2 text-black" />
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-white truncate">{it.summary}</div>
                <div className="text-[10px] text-white/40 mt-0.5">
                  <span className="uppercase tracking-wider">{it.entity_type}</span>
                  <span className="mx-1.5 text-white/20">·</span>
                  {fmtDateRelative(it.created_at)}
                  {!compact && <> <span className="mx-1.5 text-white/20">·</span> {fmtDate(it.created_at)}</>}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
